import { ListChecks, X } from "lucide-react";
import { FC } from "react";
import { FormattedMessage } from "react-intl";
import { Button } from "~/components/ui/button";
import { Checkbox } from "~/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import { useFirstStepsStore } from "../stores/useFirstStepsStore";

const FIRST_STEPS = [
  {
    id: "review-report",
    labelKey: "firstSteps.reviewReport",
  },
  {
    id: "browse-documents",
    labelKey: "firstSteps.browseDocuments",
  },
  {
    id: "try-chat",
    labelKey: "firstSteps.tryChat",
  },
] as const;

export const FirstStepsChecklist: FC = () => {
  const isDismissed = useFirstStepsStore((state) => state.isDismissed);
  const checkedItems = useFirstStepsStore((state) => state.checkedItems);
  const dismiss = useFirstStepsStore((state) => state.dismiss);
  const toggleItem = useFirstStepsStore((state) => state.toggleItem);

  if (isDismissed) {
    return null;
  }

  const completedCount = checkedItems.length;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-1.5 text-purple-600">
          <ListChecks className="h-4 w-4" />
          <span className="hidden md:inline">
            <FormattedMessage id="firstSteps.title" />
          </span>
          <span className="text-xs text-muted-foreground">
            {completedCount}/{FIRST_STEPS.length}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold">
            <FormattedMessage id="firstSteps.heading" />
          </h4>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={dismiss}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          <FormattedMessage id="firstSteps.description" />
        </p>
        <div className="space-y-2.5">
          {FIRST_STEPS.map((step) => (
            <label
              key={step.id}
              className="flex items-start gap-2.5 cursor-pointer"
            >
              <Checkbox
                checked={checkedItems.includes(step.id)}
                onCheckedChange={() => toggleItem(step.id)}
                className="mt-0.5"
              />
              <span
                className={`text-sm leading-tight ${checkedItems.includes(step.id) ? "text-muted-foreground line-through" : "text-foreground"}`}
              >
                <FormattedMessage id={step.labelKey} />
              </span>
            </label>
          ))}
        </div>
        {completedCount === FIRST_STEPS.length && (
          <div className="mt-3 pt-3 border-t border-border">
            <p className="text-xs text-muted-foreground text-center">
              <FormattedMessage id="firstSteps.allDone" />
            </p>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
